import styled from '@emotion/styled/macro'
import { Box } from '@mui/material'
import { keyframes } from '@emotion/react'
import theme from 'theme'

const shimmer = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(800%); }
`

export const CPISkeletonCard = styled(Box)`
  width: 100%;
  height: 377px;
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 27px;
    height: 100%;
    border-radius: ${theme.attributes.borderRadius.tertiary};
    background: linear-gradient(
      270deg,
      rgba(48, 40, 47, 0.5) 0%,
      rgba(87, 82, 86, 0.5) 50%,
      rgba(48, 40, 47, 0.5) 100%
    );
    animation: ${shimmer} 1.5s ease-in-out infinite;
  }

  /* Matches the dashboard loading state: light placeholder with a white sweep. */
  html[data-theme='light'] & {
    background: ${theme.colors.legacyPrimaryLight3};

    &::after {
      background: linear-gradient(
        270deg,
        rgba(252, 247, 251, 0) 0%,
        rgba(252, 247, 251, 0.9) 50%,
        rgba(252, 247, 251, 0) 100%
      );
    }
  }
`

export const CPIsWrapper = styled(Box)`
  background: ${theme.colors.surfaceContainerBackground};
  border-radius: ${theme.attributes.borderRadius.tertiary};
  padding: ${theme.spacing.medium}px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.medium}px;

  .section-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    .header-left {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing.xSmall}px;

      .title-row {
        display: flex;
        align-items: center;
        gap: ${theme.spacing.xSmall}px;
        height: 28px;

        .title-icon {
          width: 18px;
          height: 18px;
          color: ${theme.colors.menuItemText};
        }

        .title-text {
          font-family: ${theme.typography.fontFamily.primary};
          font-weight: ${theme.typography.fontWeight.bold};
          font-size: ${theme.typography.fontSize.header};
          color: ${theme.colors.menuItemText};

          html[data-theme='light'] & {
            color: #1a1518;
          }
        }
      }

      .last-update {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.normal};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.dropdownLabel};
        line-height: 22px;

        html[data-theme='light'] & {
          color: #6b6b76;
        }

        .date-value {
          color: ${theme.colors.menuItemText};
        }
      }
    }

    .toggle-container {
      display: flex;
      align-items: center;
      gap: 2px;
      background: ${theme.colors.borderMedium};
      border-radius: ${theme.attributes.borderRadius.secondary};
      padding: 2px;

      html[data-theme='light'] & {
        background: ${theme.colors.strokePrimary};
      }

      .toggle-button {
        width: 108px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        border: none;
        cursor: pointer;
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.medium};
        line-height: 20px;
        background: transparent;
        color: ${theme.colors.devicesSecondaryText};
        transition: all 0.2s ease;

        html[data-theme='light'] & {
          color: ${theme.colors.textCaption};
        }

        &.active {
          background: ${theme.colors.dropdownBorder};
          border: 1px solid ${theme.colors.dropdownBorder};
          color: ${theme.colors.devicesSecondaryText};

          html[data-theme='light'] & {
            background: ${theme.colors.surfaceContainerBackground};
            border-color: ${theme.colors.surfaceContainerBackground};
            color: ${theme.colors.cta};
          }
        }
      }
    }
  }

  .empty-state {
    min-height: 200px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    text-align: center;

    svg {
      margin-bottom: 10px;
    }

    .empty-title {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.xLarge};
      color: ${theme.colors.menuItemText};
      line-height: normal;
    }

    .empty-description {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.normal};
      font-size: ${theme.typography.fontSize.large};
      color: ${theme.colors.devicesSecondaryText};
      line-height: 20px;
    }
  }

  .cards-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
    max-height: 894px;
    overflow-y: auto;
    padding-right: 4px;

    &::-webkit-scrollbar {
      background: transparent;
      width: 20px !important;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${theme.colors.borderMedium};
      border-radius: 9999px;
      border-right: 8px solid transparent;
      border-left: 8px solid transparent;
      background-clip: padding-box;
    }
  }
`

export const ActiveCPICardWrapper = styled(Box)`
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
  display: flex;
  flex-direction: column;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover {
    background: ${theme.colors.cardFillTertiary};
    border-color: ${theme.colors.strokePrimary};
  }

  html[data-theme='light'] & {
    background: #fafafb;
    border-color: #f0f0f1;

    &:hover {
      background: ${theme.colors.cardFillTertiary};
      border-color: ${theme.colors.strokePrimary};
    }
  }

  .card-header {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
    padding: ${theme.spacing.small}px;
    border-bottom: 1px solid ${theme.colors.borderMedium};

    .cpi-chip {
      background: ${theme.colors.btnTertiaryBg};
      border-radius: 18px;
      padding: 2px ${theme.spacing.xSmall}px;
      height: 24px;
      width: 72px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      span {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.medium};
        color: ${theme.colors.devicesSecondaryText};
        letter-spacing: 0.5px;
        line-height: 16px;
      }

      html[data-theme='light'] & {
        background: #e4e4e9;

        span {
          color: #4a4a54;
        }
      }
    }

    .cpi-name {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.xLarge};
      color: ${theme.colors.menuItemText};
      line-height: 24px;
      letter-spacing: 0.1px;
      cursor: pointer;
    }
  }

  .card-body {
    padding: ${theme.spacing.small}px 16px 20px;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.medium}px;

    .performance-section {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing.xSmall}px;

      .performance-row {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;

        .perf-left {
          display: flex;
          align-items: flex-end;
          gap: 6px;

          .perf-label {
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.normal};
            font-size: ${theme.typography.fontSize.large};
            color: ${theme.colors.devicesSecondaryText};
            line-height: 22px;
          }

          .perf-value {
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.bold};
            font-size: ${theme.typography.fontSize.large};
            color: ${theme.colors.menuItemText};
            line-height: 20px;

            &.pending {
              display: inline-flex;
              align-items: center;
              gap: 6px;
            }
          }
        }

        .perf-right {
          display: flex;
          align-items: flex-end;
          gap: 6px;

          .month-label {
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.normal};
            font-size: ${theme.typography.fontSize.large};
            color: ${theme.colors.dropdownLabel};
            line-height: 20px;
            letter-spacing: 0.25px;

            html[data-theme='light'] & {
              color: ${theme.colors.textCaption};
            }
          }

          .change-indicator {
            display: flex;
            align-items: center;
            gap: 4px;

            .arrow-icon {
              display: block;
              flex-shrink: 0;
              width: 14px;
              height: 14px;
              stroke-width: 2;
            }

            .change-text {
              font-family: ${theme.typography.fontFamily.primary};
              font-weight: ${theme.typography.fontWeight.bold};
              font-size: ${theme.typography.fontSize.large};
              line-height: 20px;
            }

            &.positive {
              color: ${theme.colors.statusValueGood};
              .change-text {
                color: ${theme.colors.statusValueGood};
              }

              html[data-theme='light'] & {
                color: #1d875a;
                .change-text {
                  color: #1d875a;
                }
              }
            }

            &.negative {
              color: #e74c3c;
              .arrow-icon {
                transform: rotate(180deg);
              }
              .change-text {
                color: #e74c3c;
              }

              html[data-theme='light'] & {
                color: ${theme.colors.statusValueBad};
                .change-text {
                  color: ${theme.colors.statusValueBad};
                }
              }
            }

          }
        }
      }

      .progress-bar {
        width: 100%;
        height: 8px;
        background: ${theme.colors.gaugeTrack};
        border-radius: 3px;
        position: relative;
        overflow: hidden;

        .progress-fill {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: ${theme.colors.cta};
          border-radius: 3px;
        }
      }
    }

    .metrics {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing.small}px;

      .metric-item {
        display: flex;
        align-items: center;
        gap: ${theme.spacing.sm}px;

        .metric-icon-box {
          width: 48px;
          height: 48px;
          min-width: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${theme.colors.btnTertiaryBg};
          border-radius: ${theme.attributes.borderRadius.tertiary};

          svg {
            width: 18px;
            height: 18px;
            color: ${theme.colors.dropdownLabel};
          }
        }

        .metric-content {
          display: flex;
          flex-direction: column;
          gap: 4px;

          .metric-label {
            display: flex;
            align-items: center;
            gap: 4px;
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.normal};
            font-size: ${theme.typography.fontSize.large};
            color: ${theme.colors.devicesSecondaryText};
            line-height: 22px;

            html[data-theme='light'] & {
              color: ${theme.colors.textCaption};
            }

            .info-icon-wrapper {
              display: flex;
              cursor: default;
              color: #6c5b67;

              &:hover {
                color: ${theme.colors.buttonHollowHover};
              }
            }
          }

          .metric-value {
            display: flex;
            align-items: center;
            gap: ${theme.spacing.xSmall}px;
            font-family: ${theme.typography.fontFamily.primary};
            font-weight: ${theme.typography.fontWeight.bold};
            font-size: ${theme.typography.fontSize.large};
            color: ${theme.colors.menuItemText};
            line-height: 20px;

            .metric-sub {
              font-weight: ${theme.typography.fontWeight.normal};
              color: ${theme.colors.dropdownLabel};
              letter-spacing: 0.25px;
            }
          }

          .recent-finding-row {
            display: flex;
            align-items: center;
            gap: ${theme.spacing.small}px;
          }

          .recent-finding-change {
            display: flex;
            align-items: center;
            gap: 4px;

            .change-indicator {
              display: flex;
              align-items: center;
              gap: 4px;

              .arrow-icon {
                display: block;
                flex-shrink: 0;
                width: 12px;
                height: 12px;
                stroke-width: 2;

                &.down {
                  transform: rotate(180deg);
                }
              }

              .change-text {
                font-family: ${theme.typography.fontFamily.primary};
                font-weight: ${theme.typography.fontWeight.bold};
                font-size: ${theme.typography.fontSize.medium};
                line-height: 16px;
              }

              &.positive {
                color: ${theme.colors.statusValueGood};
                .change-text {
                  color: ${theme.colors.statusValueGood};
                }
              }

              &.negative {
                color: #e74c3c;
                .change-text {
                  color: #e74c3c;
                }
              }

            }

            .change-empty {
              font-family: ${theme.typography.fontFamily.primary};
              font-weight: ${theme.typography.fontWeight.bold};
              font-size: ${theme.typography.fontSize.medium};
              color: ${theme.colors.dropdownLabel};
              line-height: 16px;
            }

            .since-label {
              font-family: ${theme.typography.fontFamily.primary};
              font-weight: ${theme.typography.fontWeight.normal};
              font-size: ${theme.typography.fontSize.medium};
              color: ${theme.colors.dropdownLabel};
              line-height: 16px;
              letter-spacing: 0.25px;
            }
          }
        }
      }
    }
  }
`

export const AvailableCPICardWrapper = styled(Box)`
  background: ${theme.colors.labelCardBg};
  border: 1px solid ${theme.colors.borderMedium};
  border-radius: ${theme.attributes.borderRadius.secondary};
  display: flex;
  flex-direction: column;

  html[data-theme='light'] & {
    background: #fafafb;
    border-color: #f0f0f1;
  }

  .card-header {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.small}px;
    padding: ${theme.spacing.small}px;
    border-bottom: 1px solid ${theme.colors.borderMedium};

    .cpi-chip {
      background: ${theme.colors.btnTertiaryBg};
      border-radius: 18px;
      padding: 2px ${theme.spacing.xSmall}px;
      height: 24px;
      width: 72px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      span {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.medium};
        color: ${theme.colors.devicesSecondaryText};
        letter-spacing: 0.5px;
        line-height: 16px;
      }

      html[data-theme='light'] & {
        background: #e4e4e9;

        span {
          color: #4a4a54;
        }
      }
    }

    .cpi-name {
      font-family: ${theme.typography.fontFamily.primary};
      font-weight: ${theme.typography.fontWeight.bold};
      font-size: ${theme.typography.fontSize.xLarge};
      color: ${theme.colors.menuItemText};
      line-height: 24px;
      letter-spacing: 0.1px;
      cursor: pointer;
    }
  }

  .card-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: ${theme.spacing.small}px;

    .description-section {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .description-label {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.dropdownLabel};
        line-height: 20px;
      }

      .description-text {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.medium};
        color: ${theme.colors.devicesSecondaryText};
        line-height: 16px;
      }
    }

    .data-sources-section {
      display: flex;
      flex-direction: column;
      gap: 10px;

      .data-sources-label {
        font-family: ${theme.typography.fontFamily.primary};
        font-weight: ${theme.typography.fontWeight.bold};
        font-size: ${theme.typography.fontSize.large};
        color: ${theme.colors.dropdownLabel};
        line-height: 20px;
      }

      .data-sources-logos {
        display: flex;
        padding-right: ${theme.spacing.small}px;

        img {
          width: 40px;
          height: 40px;
          border-radius: ${theme.attributes.borderRadius.circle};
          margin-right: -${theme.spacing.small}px;
          box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.3), 0px 8px 12px 0px rgba(0, 0, 0, 0.15);
        }
      }
    }
  }
`
